import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  User, 
  Mail, 
  Phone, 
  Building2,
  Lock,
  Save,
  Eye,
  EyeOff,
  Loader2,
  CheckCircle,
  CreditCard
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ResidentProfilePage() {
  const { user, refreshUser, isSimulationActive, stopRoleSimulation } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [card, setCard] = useState(null);
  
  // Simülasyon kontrolü
  const simulationActive = isSimulationActive ? isSimulationActive() : false;
  
  // Simülasyonu bitir
  const handleStopSimulation = () => {
    if (stopRoleSimulation) {
      stopRoleSimulation();
      navigate('/dashboard');
    }
  };

  const [profileForm, setProfileForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    apartment_number: ''
  });

  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    if (simulationActive) {
      // Simülasyonda örnek anonim bilgiler göster
      setProfileForm({
        full_name: 'Örnek Kullanıcı',
        email: 'ornek@email.com',
        phone: '05XX XXX XX XX',
        apartment_number: 'A-101'
      });
    } else if (user) {
      setProfileForm({
        full_name: user.full_name || '',
        email: user.email || '',
        phone: user.phone || '',
        apartment_number: user.apartment_number || ''
      });
    }
  }, [user, simulationActive]);

  useEffect(() => {
    const fetchCard = async () => {
      if (simulationActive) return; // Simülasyonda kart bilgisi çekme
      if (user?.id) {
        try {
          const token = localStorage.getItem('hims_token');
          const response = await fetch(`${API_URL}/api/rfid/user/${user.id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const cards = await response.json();
            setCard(cards?.[0] || null);
          }
        } catch (err) {
          console.error('Kart bilgisi alınamadı:', err);
        }
      }
    };
    fetchCard();
  }, [user?.id, simulationActive]);

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSavingProfile(true);
      const token = localStorage.getItem('hims_token');

      const response = await fetch(`${API_URL}/api/users/${user?.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          full_name: profileForm.full_name,
          phone: profileForm.phone,
          apartment_number: profileForm.apartment_number
        })
      });

      if (response.ok) {
        toast.success('Profil bilgileriniz güncellendi');
        if (refreshUser) {
          refreshUser();
        }
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Profil güncellenemedi');
      }
    } catch (err) {
      toast.error('Bir hata oluştu');
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Yeni şifreler eşleşmiyor');
      return;
    }

    if (passwordForm.new_password.length < 6) {
      toast.error('Şifre en az 6 karakter olmalıdır');
      return;
    }

    try {
      setSavingPassword(true);
      const token = localStorage.getItem('hims_token');

      const response = await fetch(`${API_URL}/api/users/${user?.id}/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password
        })
      });

      if (response.ok) {
        toast.success('Şifreniz başarıyla değiştirildi');
        setPasswordForm({
          current_password: '',
          new_password: '',
          confirm_password: ''
        });
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Şifre değiştirilemedi');
      }
    } catch (err) {
      toast.error('Bir hata oluştu');
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="resident-profile">
      {/* Simülasyon Modu Banner */}
      {simulationActive && (
        <div className="bg-red-600 text-white py-3 px-4 rounded-xl flex items-center justify-between shadow-lg">
          <div className="flex items-center gap-3">
            <Eye className="w-5 h-5 animate-pulse" />
            <span className="font-bold">SİMÜLASYON MODU:</span>
            <span className="font-medium">Site Sakini görünümü</span>
          </div>
          <button
            onClick={handleStopSimulation}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors text-sm font-medium"
            data-testid="stop-simulation-profile-btn"
          >
            <EyeOff className="w-4 h-4" />
            Simülasyonu Bitir
          </button>
        </div>
      )}
      
      {/* Simülasyon Uyarısı */}
      {simulationActive && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-800">
              <Eye className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h3 className="font-semibold text-amber-800 dark:text-amber-200">Simülasyon Modunda Görüntüleme</h3>
              <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                Bu sayfa Site Sakini rolünün göreceği profil ekranını simüle ediyor. 
                Gerçek kullanıcı bilgileri gösterilmektedir, düzenleme yapılamaz.
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Profilim</h1>
        <p className="text-slate-500 dark:text-slate-400">Kişisel bilgilerinizi görüntüleyin ve düzenleyin</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sol Kolon - Profil Özeti */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 text-center">
            {/* Avatar */}
            <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-3xl font-bold">
              {user?.full_name
                ? user.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                : user?.email?.[0]?.toUpperCase() || 'U'}
            </div>

            <h2 className="text-xl font-semibold text-slate-800 dark:text-white mb-1">
              {user?.full_name || user?.email}
            </h2>
            <p className="text-slate-500 dark:text-slate-400 mb-4">Site Sakini</p>

            {/* Kart Bilgisi */}
            {card && (
              <div className="mt-4 p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <CreditCard className="w-5 h-5 text-blue-500" />
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">RFID Kartım</span>
                </div>
                <p className="font-mono text-lg text-slate-800 dark:text-white">{card.card_id}</p>
                <p className={`text-sm mt-1 ${card.is_active || card.status === 'active' ? 'text-emerald-600' : 'text-red-600'}`}>
                  {card.is_active || card.status === 'active' ? 'Aktif' : 'Bloke'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Sağ Kolon - Formlar */}
        <div className="lg:col-span-2 space-y-6">
          {/* Profil Bilgileri Formu */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
            <div className="p-5 border-b border-slate-200 dark:border-slate-700">
              <h2 className="text-lg font-semibold text-slate-800 dark:text-white flex items-center gap-2">
                <User className="w-5 h-5 text-blue-500" />
                Profil Bilgileri
              </h2>
            </div>
            <form onSubmit={handleProfileSubmit} className="p-5 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Ad Soyad
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="text"
                      value={profileForm.full_name}
                      onChange={(e) => setProfileForm(prev => ({ ...prev, full_name: e.target.value }))}
                      disabled={simulationActive}
                      className={`w-full pl-10 pr-4 py-3 rounded-xl border border-slate-300 dark:border-slate-600 ${simulationActive ? 'bg-slate-100 dark:bg-slate-600 cursor-not-allowed' : 'bg-white dark:bg-slate-700'} text-slate-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    E-posta
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="email"
                      value={profileForm.email}
                      disabled
                      className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-600 text-slate-500 dark:text-slate-400 cursor-not-allowed"
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">E-posta değiştirilemez</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Telefon
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="tel"
                      value={profileForm.phone}
                      onChange={(e) => setProfileForm(prev => ({ ...prev, phone: e.target.value }))}
                      disabled={simulationActive}
                      placeholder="05XX XXX XX XX"
                      className={`w-full pl-10 pr-4 py-3 rounded-xl border border-slate-300 dark:border-slate-600 ${simulationActive ? 'bg-slate-100 dark:bg-slate-600 cursor-not-allowed' : 'bg-white dark:bg-slate-700'} text-slate-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Daire No
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="text"
                      value={profileForm.apartment_number}
                      onChange={(e) => setProfileForm(prev => ({ ...prev, apartment_number: e.target.value }))}
                      disabled={simulationActive}
                      placeholder="Örn: 5"
                      className={`w-full pl-10 pr-4 py-3 rounded-xl border border-slate-300 dark:border-slate-600 ${simulationActive ? 'bg-slate-100 dark:bg-slate-600 cursor-not-allowed' : 'bg-white dark:bg-slate-700'} text-slate-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                {!simulationActive && (
                <button
                  type="submit"
                  disabled={savingProfile}
                  className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {savingProfile ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Save className="w-5 h-5" />
                  )}
                  Kaydet
                </button>
                )}
                {simulationActive && (
                  <span className="text-sm text-slate-500 dark:text-slate-400 italic">
                    Simülasyon modunda düzenleme yapılamaz
                  </span>
                )}
              </div>
            </form>
          </div>

          {/* Şifre Değiştirme Formu - Simülasyonda gizle */}
          {!simulationActive && (
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
            <div className="p-5 border-b border-slate-200 dark:border-slate-700">
              <h2 className="text-lg font-semibold text-slate-800 dark:text-white flex items-center gap-2">
                <Lock className="w-5 h-5 text-amber-500" />
                Şifre Değiştir
              </h2>
            </div>
            <form onSubmit={handlePasswordSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Mevcut Şifre
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={passwordForm.current_password}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, current_password: e.target.value }))}
                    className="w-full pl-10 pr-12 py-3 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Yeni Şifre
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      value={passwordForm.new_password}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, new_password: e.target.value }))}
                      className="w-full pl-10 pr-12 py-3 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Yeni Şifre (Tekrar)
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      value={passwordForm.confirm_password}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm_password: e.target.value }))}
                      className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={savingPassword}
                  className="flex items-center gap-2 px-6 py-3 bg-amber-600 text-white rounded-xl hover:bg-amber-700 transition-colors disabled:opacity-50"
                >
                  {savingPassword ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <CheckCircle className="w-5 h-5" />
                  )}
                  Şifreyi Değiştir
                </button>
              </div>
            </form>
          </div>
          )}
        </div>
      </div>
    </div>
  );
}
